import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const GasFeeEstimation = ({ isVisible, url, onConfirm, onCancel }) => {
  const [gasEstimate, setGasEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [maticPrice, setMaticPrice] = useState(1.85); // Mock MATIC price in USD

  useEffect(() => {
    if (isVisible && url) {
      estimateGasFee();
    }
  }, [isVisible, url]);

  const estimateGasFee = async () => {
    setIsLoading(true);
    
    try {
      // Simulate gas estimation API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockEstimate = {
        gasLimit: 150000,
        gasPrice: 30, // Gwei
        totalGas: 0.0045, // MATIC
        priority: 'standard'
      };
      
      setGasEstimate(mockEstimate);
    } catch (error) {
      console.error('Gas estimation failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateUsdValue = (maticAmount) => {
    return (maticAmount * maticPrice)?.toFixed(4);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Confirm Transaction</h3>
          <button
            onClick={onCancel}
            className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* URL Preview */}
          <div className="mb-6">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Notarizing URL
            </label>
            <div className="p-3 bg-muted/50 rounded-md border">
              <p className="text-sm text-foreground font-mono break-all">{url}</p>
            </div>
          </div>

          {/* Gas Estimation */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Gas Fee Estimation</h4>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Estimating gas fees...</span>
              </div>
            ) : gasEstimate ? (
              <div className="space-y-3">
                {/* Gas Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gas Limit:</span>
                    <span className="text-foreground font-mono">{gasEstimate?.gasLimit?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gas Price:</span>
                    <span className="text-foreground font-mono">{gasEstimate?.gasPrice} Gwei</span>
                  </div>
                </div>

                {/* Total Cost */}
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Total Gas Fee</p>
                      <p className="text-xs text-muted-foreground">Standard priority</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-foreground">
                        {gasEstimate?.totalGas} MATIC
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ≈ ${calculateUsdValue(gasEstimate?.totalGas)} USD
                      </p>
                    </div>
                  </div>
                </div>

                {/* Network Info */}
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <Icon name="Zap" size={12} />
                  <span>Polygon Network • MATIC Price: ${maticPrice}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Icon name="AlertCircle" size={24} className="text-destructive mx-auto mb-2" />
                <p className="text-sm text-destructive">Failed to estimate gas fees</p>
                <button
                  onClick={estimateGasFee}
                  className="text-xs text-primary hover:text-primary/80 mt-1"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3 p-6 border-t border-border">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted transition-smooth"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!gasEstimate || isLoading}
            className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
          >
            Confirm & Pay
          </button>
        </div>
      </div>
    </div>
  );
};

export default GasFeeEstimation;